import { useCallback, useEffect, useState } from "react";
import { api, type MoneyPanel, type MoneyLine } from "../api.ts";
import { C, mono, dateMaybeYear, shortDate } from "../theme.ts";
import { useRefresh } from "../components/PullToRefresh.tsx";

// Four sections. One of them is computed from the board and the other three
// are kept by hand.
//
// The copy rules hold here as everywhere else: state the amount and the date,
// skip the verdict. Nothing is totalled into a single number, nothing is red,
// and nothing on this screen tells him whether he can afford something. Real
// amounts here are ranges and unknowns — a total would be a made-up number
// presented with more confidence than any of its parts.

const Label = ({ children }: { children: React.ReactNode }) => (
  <p style={{ fontFamily: mono, fontSize: 11, color: C.inkFaint, letterSpacing: 1, margin: "26px 0 8px" }}>
    {children}
  </p>
);

const card: React.CSSProperties = {
  background: C.card,
  borderRadius: 12,
  border: `1px solid ${C.rule}`,
  padding: "12px 14px",
  marginBottom: 8,
};

function Line({ l, onOpen }: { l: MoneyLine; onOpen: (id: string) => void }) {
  return (
    <div style={card}>
      <div style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
        <div style={{ fontSize: 14, lineHeight: 1.4, flex: 1, minWidth: 0 }}>{l.label}</div>
        {l.amount && (
          <div style={{ fontFamily: mono, fontSize: 12.5, color: C.inkSoft, textAlign: "right", maxWidth: "60%" }}>
            {l.amount}
          </div>
        )}
      </div>

      {l.worst_case && (
        <div style={{ fontSize: 12.5, color: C.inkSoft, lineHeight: 1.55, marginTop: 8 }}>
          <span style={{ fontFamily: mono, fontSize: 11, color: C.inkFaint }}>worst case · </span>
          {l.worst_case}
        </div>
      )}

      {l.note && (
        <div style={{ fontSize: 12.5, color: C.inkFaint, lineHeight: 1.5, marginTop: 6 }}>{l.note}</div>
      )}

      {/* Where a task on the board keeps this figure, point at it rather than
          letting the two drift apart. */}
      {l.task_id && (
        <div
          onClick={() => onOpen(l.task_id!)}
          style={{ fontFamily: mono, fontSize: 11, color: C.inkGhost, marginTop: 8, cursor: "pointer" }}
        >
          kept by {l.task_id}
          {l.task_state && l.task_state !== "open" ? ` · ${l.task_state}` : ""}
        </div>
      )}
    </div>
  );
}

export function Money({ onOpen }: { onOpen: (id: string) => void }) {
  const [panel, setPanel] = useState<MoneyPanel | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Lifted out of the effect so the pull gesture has something to call. The
  // panel is only replaced on a good answer, so a failed refresh leaves the
  // figures that are on screen there rather than blanking them.
  const load = useCallback(async () => {
    try { setPanel(await api.money()); setError(null); }
    catch (e) { setError((e as Error).message); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useRefresh(load);

  if (error) return <p style={{ color: C.alert, fontSize: 13, padding: "0 18px" }}>{error}</p>;
  if (!panel) return <p style={{ color: C.inkFaint, fontSize: 13, padding: "0 18px" }}>Opening…</p>;

  const { next_90_days: next } = panel;

  return (
    <div style={{ padding: "0 18px 8px" }}>
      <Label>THIS MONTH</Label>
      {panel.this_month.map((l) => <Line key={l.id} l={l} onOpen={onOpen} />)}

      <Label>THE NEXT NINETY DAYS</Label>
      {/* Computed from the board — from the tasks that already carry an amount
          and a date. Deliberately not a second place to maintain: that is how
          the markdown board went stale. */}
      {next.dated.length === 0 && (
        <p style={{ fontSize: 13, color: C.inkFaint, lineHeight: 1.55, margin: 0 }}>
          Nothing on the board carries both an amount and a date inside the window.
        </p>
      )}
      {next.dated.map((t) => (
        <div
          key={t.id}
          onClick={() => onOpen(t.id)}
          style={{ ...card, borderLeft: `4px solid ${t.value_colour ?? C.ruleStrong}`, cursor: "pointer" }}
        >
          <div style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
            <div style={{ fontSize: 14, lineHeight: 1.4, flex: 1, minWidth: 0 }}>{t.title}</div>
            <div style={{ fontFamily: mono, fontSize: 12.5, color: C.inkSoft, textAlign: "right", maxWidth: "60%" }}>
              {t.money}
            </div>
          </div>
          <div style={{ fontFamily: mono, fontSize: 11, color: C.inkFaint, marginTop: 4 }}>
            {t.id} · {shortDate(t.due)}
            {t.state !== "open" ? ` · ${t.state}` : ""}
          </div>
        </div>
      ))}

      {/* Named rather than dropped: they are the reason the ninety-day figure
          is not the whole picture. */}
      {next.beyond.length > 0 && (
        <p style={{ fontFamily: mono, fontSize: 11, color: C.inkGhost, lineHeight: 1.8, marginTop: 10 }}>
          past the window ·{" "}
          {next.beyond.map((t) => `${t.money} ${dateMaybeYear(t.due)}`).join(" · ")}
        </p>
      )}
      {next.undated.length > 0 && (
        <p style={{ fontFamily: mono, fontSize: 11, color: C.inkGhost, lineHeight: 1.8 }}>
          no date on them yet ·{" "}
          {next.undated.map((t) => `${t.money} (${t.id})`).join(" · ")}
        </p>
      )}

      <Label>WHAT COULD STILL SURPRISE ME</Label>
      {panel.surprises.map((l) => <Line key={l.id} l={l} onOpen={onOpen} />)}

      <Label>OWED TO ME</Label>
      {panel.owed.map((l) => <Line key={l.id} l={l} onOpen={onOpen} />)}

      <p style={{ fontFamily: mono, fontSize: 10.5, color: C.inkGhost, lineHeight: 1.7, margin: "26px 0 0" }}>
        the ninety days is read from the board · the rest was seeded on 22.08 and is yours to correct (T-135)
      </p>
    </div>
  );
}
